import { t as uiText } from '../i18n';
// src/components/CreateWorkspaceForm.tsx

import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuthContext } from '../context/AuthContext';
import { useWorkspaceContext } from '../context/WorkspaceContext';
import { createWorkspace } from '../features/workspace/createWorkspace';
import { updateWorkspaceIcon } from '../features/workspace/updateWorkspaceIcon';
import ImageCropModal from './ImageCropModal';
import './CreateWorkspaceForm.css';

const MAX_LENGTH = 48;
const MIN_ID_LENGTH = 6;

const getDisplayLength = (text: string): number => {
  let length = 0;
  for (let char of text) {
    length += char.charCodeAt(0) > 127 ? 2 : 1;
  }
  return length;
};

const sanitizeId = (input: string) => {
  return input.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
};

interface CreateWorkspaceFormProps {
  onClose?: () => void;
}

const CreateWorkspaceForm: React.FC<CreateWorkspaceFormProps> = ({ onClose }) => {
  const { user } = useAuthContext();
  const { refresh } = useWorkspaceContext();
  const navigate = useNavigate();

  const [creationMode, setCreationMode] = useState<'new' | 'child' | null>(null);

  const [name, setName] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [visibility, setVisibility] = useState(0);
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [selectedParentWorkspaceId, setSelectedParentWorkspaceId] = useState<string>('');
  const [parentWorkspaces, setParentWorkspaces] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingParentWorkspaces, setLoadingParentWorkspaces] = useState(false);

  const userTypedWorkspaceId = useRef(false);

  const isNewMode = creationMode === 'new';
  const isChildMode = creationMode === 'child';
  const canShowWorkspaceFields = isNewMode || (isChildMode && !!selectedParentWorkspaceId);

  useEffect(() => {
    const loadParentWorkspaces = async () => {
      if (!user?.uid) return;

      setLoadingParentWorkspaces(true);
      try {
        const userWorkspacesRef = collection(db, `users/${user.uid}/workspaces`);
        const snap = await getDocs(userWorkspacesRef);
        const workspaceList: Array<{ id: string; name: string }> = [];

        for (const docSnap of snap.docs) {
          const workspaceId = docSnap.id;
          try {
            const workspaceDoc = await getDoc(doc(db, 'workspaces', workspaceId));
            if (workspaceDoc.exists()) {
              const data = workspaceDoc.data();
              if (!data?.parentWorkspaceId) {
                workspaceList.push({
                  id: workspaceId,
                  name: data?.name || workspaceId,
                });
              }
            }
          } catch (error) {
            console.error(`Failed to load workspace ${workspaceId}:`, error);
          }
        }

        setParentWorkspaces(workspaceList);
      } catch (error) {
        console.error('Failed to load parent workspaces:', error);
      } finally {
        setLoadingParentWorkspaces(false);
      }
    };

    void loadParentWorkspaces();
  }, [user?.uid]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    if (getDisplayLength(input) > MAX_LENGTH) return;
    setName(input);

    if (!userTypedWorkspaceId.current) {
      const nextId = sanitizeId(input).slice(0, MAX_LENGTH);
      setWorkspaceId(nextId);
      if (nextId && nextId.length < MIN_ID_LENGTH) {
        setErrorMsg(uiText("Enter a workspace ID of at least {{MIN_ID_LENGTH}} characters", { MIN_ID_LENGTH: MIN_ID_LENGTH }));
      } else {
        setErrorMsg('');
      }
    }
  };

  const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = sanitizeId(e.target.value).slice(0, MAX_LENGTH);
    userTypedWorkspaceId.current = true;
    setWorkspaceId(input);
    if (input && input.length < MIN_ID_LENGTH) {
      setErrorMsg(uiText("Enter a workspace ID of at least {{MIN_ID_LENGTH}} characters", { MIN_ID_LENGTH: MIN_ID_LENGTH }));
    } else {
      setErrorMsg('');
    }
  };

  const handleCreate = async () => {
    if (!user || !workspaceId || !name) return;
    if (workspaceId.length < MIN_ID_LENGTH) {
      setErrorMsg(uiText("Enter a workspace ID of at least {{MIN_ID_LENGTH}} characters", { MIN_ID_LENGTH: MIN_ID_LENGTH }));
      return;
    }
    setSubmitting(true);
    setErrorMsg('');

    try {
      const result = await createWorkspace({
        id: workspaceId,
        name,
        createdBy: user.uid,
        visibility,
        planId: isChildMode ? undefined : 'free',
        seriesId: isChildMode ? undefined : 'NCN',
        parentWorkspaceId: isChildMode ? (selectedParentWorkspaceId || undefined) : undefined,
      });

      if (!result.success && result.error) {
        setErrorMsg(result.error);
        return;
      }

      if (iconFile) {
        await updateWorkspaceIcon({
          workspaceId: workspaceId,
          imageFile: iconFile,
          currentIconPath: null,
        });
      }

      await refresh();
      alert(uiText("Workspace created"));
      navigate(`/${workspaceId}`);
      onClose?.();
    } catch (error) {
      console.error('ワークスペース作成エラー:', error);
      setErrorMsg(uiText("Failed to create the workspace"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <form style={{ maxWidth: '440px', width: '100%' }}>
      {!creationMode ? (
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
            {uiText("Choose how to create")}<span style={{ color: 'red' }}>*</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button
              type="button"
              onClick={() => {
                setCreationMode('new');
                setSelectedParentWorkspaceId('');
              }}
              disabled={submitting}
              className="cw-button cw-free-try"
            >
              {uiText("Try it free")}
            </button>

            <button
              type="button"
              onClick={() => {
                setCreationMode('child');
                setSelectedParentWorkspaceId('');
              }}
              disabled={submitting}
              className="cw-button cw-link-choice"
            >
              {uiText("Create as a child workspace of an existing workspace")}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
              {uiText("Creation method:")} {creationMode === 'new' ? uiText("Create new") : uiText("Child workspace")}
            </div>
            <button
              type="button"
              onClick={() => {
                setCreationMode(null);
                setSelectedParentWorkspaceId('');
              }}
              disabled={submitting}
              style={{
                padding: '4px 8px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                color: '#666',
                textDecoration: 'underline',
              }}
            >
              {uiText("Change")}
            </button>
          </div>
          {isNewMode && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              {uiText("Plan:")} <strong style={{ color: '#2b2b2b' }}>free</strong>
              <div style={{ marginTop: '4px' }}>{uiText("You can change the plan after creation.")}</div>
            </div>
          )}
        </div>
      )}

      {isChildMode && (
        <div style={{ marginBottom: '24px' }}>
          <label htmlFor="parentWorkspace" style={{ fontSize: '14px', fontWeight: 'bold' }}>
            {uiText("Select parent workspace")}<span style={{ color: 'red' }}>*</span>
          </label>
          {loadingParentWorkspaces ? (
            <div style={{ marginTop: '4px', padding: '8px', color: '#666' }}>{uiText("Loading now...")}</div>
          ) : parentWorkspaces.length === 0 ? (
            <div style={{ marginTop: '4px', padding: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
              <div style={{ color: '#856404', fontSize: '14px' }}>
                {uiText("You are not in any workspace")}
              </div>
            </div>
          ) : (
            <select
              id="parentWorkspace"
              value={selectedParentWorkspaceId}
              onChange={(e) => setSelectedParentWorkspaceId(e.target.value)}
              disabled={submitting}
              style={{
                width: '100%',
                padding: '8px',
                marginTop: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                backgroundColor: '#fff',
                fontSize: '14px',
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              <option value="">{uiText("Please select")}</option>
              {parentWorkspaces.map((workspace) => (
                <option key={workspace.id} value={workspace.id}>
                  {workspace.name}
                </option>
              ))}
            </select>
          )}
          <div style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
            {uiText("A child workspace is linked to the parent workspace's organization account.")}
          </div>
        </div>
      )}

      {canShowWorkspaceFields && (
        <>
      <div style={{ marginBottom: '16px' }}>
        <label htmlFor="name">
          {uiText("Workspace name")}<span style={{ color: 'red' }}>*</span>
        </label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={handleNameChange}
          placeholder={uiText("Example: a sales team")}
          style={{
            width: '96%',
            padding: '8px',
            marginTop: '4px',
            borderRadius: '4px',
            border: '1px solid #ccc',
          }}
        />
        <div style={{ textAlign: 'right', fontSize: '12px', color: '#555' }}>
          {getDisplayLength(name)}/{MAX_LENGTH}
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label htmlFor="id">
          {uiText("Workspace ID")}<span style={{ color: 'red' }}>*</span>
        </label>
        <input
          id="id"
          type="text"
          value={workspaceId}
          onChange={handleIdChange}
          placeholder={uiText("Letters and numbers only, at least {{MIN_ID_LENGTH}} characters (auto-generated, editable)", { MIN_ID_LENGTH: MIN_ID_LENGTH })}
          style={{
            width: '96%',
            padding: '8px',
            marginTop: '4px',
            borderRadius: '4px',
            border: '1px solid #ccc',
            backgroundColor: '#fff',
          }}
        />
        <div style={{ textAlign: 'right', fontSize: '12px', color: '#555' }}>
          {workspaceId.length}/{MAX_LENGTH}
        </div>
        {errorMsg && (
          <div style={{ color: 'red', marginTop: '4px', fontSize: '14px' }}>{errorMsg}</div>
        )}
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#333'
        }}>
          {uiText("Workspace icon")}
        </label>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '12px'
        }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '6px',
              border: '2px solid var(--workspace-primary, #cfae91)',
              backgroundColor: 'var(--workspace-border-soft, #f5f1ed)',
              color: 'var(--workspace-primary, #cfae91)',
              fontSize: '20px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              overflow: 'hidden'
            }}
          >
            {iconUrl ? (
              <img
                src={iconUrl}
                alt={uiText("Workspace icon")}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const textElement = e.currentTarget.nextElementSibling as HTMLElement;
                  if (textElement) {
                    textElement.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <div
              style={{
                width: '100%',
                height: '100%',
                display: iconUrl ? 'none' : 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--workspace-primary, #cfae91)'
              }}
            >
              {name?.charAt(0) || 'A'}
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
              {uiText("Preview")}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {iconUrl ? uiText("Image icon") : uiText("Default icon")}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            type="button"
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'image/*';
              input.onchange = (e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (file) {
                  setSelectedImageFile(file);
                  setShowCropModal(true);
                }
              };
              input.click();
            }}
            disabled={submitting}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--workspace-primary, #cfae91)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              opacity: submitting ? 0.6 : 1,
            }}
          >
            {uiText("Select file")}
          </button>
          <div
            style={{
              flex: 1,
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: '#f8f9fa',
              fontSize: '14px',
              color: '#666',
            }}
          >
            {iconFile ? iconFile.name : uiText("None selected")}
          </div>
        </div>

        {iconUrl && (
          <button
            type="button"
            onClick={() => {
              setIconUrl(null);
              setIconFile(null);
              setSelectedImageFile(null);
            }}
            disabled={submitting}
            style={{
              marginTop: '8px',
              padding: '4px 8px',
              backgroundColor: '#ff6b6b',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
            }}
          >
            {uiText("Delete image")}
          </button>
        )}

        <div style={{
          fontSize: '12px',
          color: '#666',
          marginTop: '4px'
        }}>
          {uiText("Select an image to display as the workspace icon")}
        </div>
      </div>
        </>
      )}

      {canShowWorkspaceFields && (
      <button
        type="button"
        onClick={handleCreate}
        disabled={
          !name ||
          !workspaceId ||
          workspaceId.length < MIN_ID_LENGTH ||
          (isChildMode && !selectedParentWorkspaceId) ||
          submitting
        }
        style={{
          width: '100%',
          padding: '10px',
          backgroundColor: (
            !name ||
            !workspaceId ||
            workspaceId.length < MIN_ID_LENGTH ||
            (isChildMode && !selectedParentWorkspaceId) ||
            submitting
          ) ? '#ccc' : 'var(--workspace-primary, #cfae91)',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          cursor: (
            !name ||
            !workspaceId ||
            workspaceId.length < MIN_ID_LENGTH ||
            (isChildMode && !selectedParentWorkspaceId) ||
            submitting
          ) ? 'not-allowed' : 'pointer',
        }}
      >
        {submitting ? uiText("Creating...") : uiText("Create")}
      </button>
      )}
    </form>
      {showCropModal && selectedImageFile && (
        <ImageCropModal
          imageFile={selectedImageFile}
          cropType="square"
          onCropComplete={(croppedFile, dataUrl) => {
            setIconFile(croppedFile);
            setIconUrl(dataUrl);
            setShowCropModal(false);
            setSelectedImageFile(null);
          }}
          onCancel={() => {
            setShowCropModal(false);
            setSelectedImageFile(null);
          }}
        />
      )}
    </>
  );
};

export default CreateWorkspaceForm;
